import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FileText, Upload, CheckCircle2, XCircle, Trash2, PlusCircle, Pencil, Save, X,
  ClipboardCheck, History, Wrench, Receipt, ListChecks, FileSearch, AlertTriangle,
  MessageSquare, Calendar as CalendarIcon,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { fmtDate, fmtDateTime, ROLE_LABELS } from "@/lib/format";
import {
  DOCUMENT_KINDS, PARTS_STATUSES, REPAIR_STATUSES, ADVANCE_MODES, PAYMENT_STATUSES,
  partsStatusLabel, repairStatusLabel, advanceModeLabel, paymentStatusLabel,
  canEditEstimate, canEditDocuments, canEditParts, canEditRepair, canEditBilling,
  canAddCrmNote, canDeleteCrmNote,
  timelineActionLabel,
} from "./avShared";
import {
  avAddEstimate, avManualApproveEstimate, avUpdateParts, avAddPartItem,
  avUpdatePartItem, avDeletePartItem, avUpdateRepair, avTimeline,
  avUploadAttachment, avAttachmentUrl, avDeleteAttachment,
  avAddAdvance, avDeleteAdvance, avUpdateBilling,
  avAddCrmNote, avDeleteCrmNote,
} from "@/lib/api";

const inputCls =
  "w-full border border-[#e2e1eb] rounded-md px-2.5 py-1.5 text-[13px] focus:outline-none focus:border-yamaha-400";

// Currency formatter used for estimate amounts. Kept local so we don't add
// to the global format.js for one consumer.
const fmtINR = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  return num.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });
};

// ============== ESTIMATE TAB ==============
export const EstimateTab = ({ av, currentUser, onChange }) => {
  const canEdit = canEditEstimate(currentUser) && av.status !== "delivered";
  const [showAdd, setShowAdd] = useState(false);
  const [approving, setApproving] = useState(null); // {est, decision}

  const estimates = useMemo(() => [...(av.estimates || [])].reverse(), [av.estimates]);
  const latest = (av.estimates || []).slice(-1)[0];

  return (
    <div className="space-y-4" data-testid="av-tab-estimate">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[14px] font-semibold text-[#1a1b22]">Estimates</div>
          <div className="text-[11.5px] text-[#747684]">All versions are preserved. Latest is shown first.</div>
        </div>
        {canEdit && (
          <Button onClick={() => setShowAdd(true)} data-testid="av-est-add-btn" className="bg-yamaha-700 hover:bg-yamaha-800">
            <PlusCircle className="w-4 h-4 mr-2" /> Add Estimate
          </Button>
        )}
      </div>

      {/* Latest estimate hero card */}
      {latest && (
        <div className="border border-yamaha-200 bg-yamaha-50/30 rounded-lg p-4 flex flex-wrap items-start gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="text-[10.5px] uppercase tracking-wider text-yamaha-700 font-bold">Active Estimate</div>
            <div className="text-[18px] font-bold text-[#1a1b22] mt-1">#{latest.estimate_number}</div>
            <div className="text-[14px] text-[#1a1b22]">{fmtINR(latest.estimate_amount)} · {fmtDate(latest.estimate_date)}</div>
            {latest.remarks && <div className="text-[11.5px] text-[#444653] mt-1">{latest.remarks}</div>}
          </div>
          <div className="flex flex-col items-end gap-2">
            <ApprovalPill est={latest} />
            {latest.pdf_attachment_id && (
              <a
                href={avAttachmentUrl(av.id, latest.pdf_attachment_id)}
                target="_blank" rel="noopener noreferrer"
                data-testid="av-est-view-pdf"
                className="text-[11.5px] inline-flex items-center gap-1 text-yamaha-700 hover:text-yamaha-800 font-semibold"
              >
                <FileSearch className="w-3.5 h-3.5" /> View PDF
              </a>
            )}
            {canEdit && latest.customer_approval_status === "pending" && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setApproving({ est: latest, decision: "rejected" })} data-testid="av-est-reject" className="border-rose-200 text-rose-700 hover:bg-rose-50">
                  <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                </Button>
                <Button size="sm" onClick={() => setApproving({ est: latest, decision: "approved" })} data-testid="av-est-approve" className="bg-emerald-600 hover:bg-emerald-700">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* History */}
      <div className="border border-[#e2e1eb] rounded-lg overflow-hidden">
        <div className="px-3 py-2 border-b border-[#e2e1eb] bg-[#faf8ff] text-[12px] font-semibold text-[#1a1b22] flex items-center gap-1.5">
          <History className="w-3.5 h-3.5" /> Version History ({estimates.length})
        </div>
        {estimates.length === 0 && (
          <div className="px-4 py-10 text-center text-[#747684] text-[12px]">
            No estimates yet. {canEdit && "Click 'Add Estimate' to add the first one."}
          </div>
        )}
        {estimates.map((e) => (
          <EstimateRow key={e.id} av={av} est={e} />
        ))}
      </div>

      <AddEstimateDialog
        open={showAdd}
        onClose={() => setShowAdd(false)}
        avId={av.id}
        currentUser={currentUser}
        onAdded={(updated) => { setShowAdd(false); onChange(updated); }}
      />

      <ManualApproveDialog
        state={approving}
        onClose={() => setApproving(null)}
        avId={av.id}
        currentUser={currentUser}
        onDone={(updated) => { setApproving(null); onChange(updated); }}
      />
    </div>
  );
};

const ApprovalPill = ({ est }) => {
  const tones = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-rose-50 text-rose-700 border-rose-200",
  };
  return (
    <span className={`inline-block text-[10.5px] font-bold uppercase tracking-wider border rounded-full px-2 py-0.5 ${tones[est.customer_approval_status] || tones.pending}`}>
      {est.customer_approval_status}{est.approved_via ? ` · ${est.approved_via}` : ""}
    </span>
  );
};

const EstimateRow = ({ av, est }) => (
  <div className={`px-3 py-2.5 border-b last:border-b-0 border-[#f1f0f7] flex items-start gap-3 ${est.is_active ? "bg-yamaha-50/20" : ""}`}>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold text-[12.5px] text-[#1a1b22]">#{est.estimate_number}</span>
        <span className="text-[12px] text-[#444653]">{fmtINR(est.estimate_amount)}</span>
        <span className="text-[11px] text-[#747684]">· {fmtDate(est.estimate_date)}</span>
        {est.is_active && (
          <span className="text-[9.5px] font-bold uppercase tracking-wider bg-yamaha-100 text-yamaha-700 rounded-full px-1.5 py-0.5">Active</span>
        )}
        <ApprovalPill est={est} />
      </div>
      <div className="text-[10.5px] text-[#747684] mt-0.5">
        Added by {est.created_by || "—"}{est.created_by_role ? ` (${ROLE_LABELS[est.created_by_role] || est.created_by_role})` : ""} · {fmtDateTime(est.created_at)}
      </div>
      {est.remarks && <div className="text-[11.5px] text-[#444653] mt-1">{est.remarks}</div>}
      {est.customer_approval_status !== "pending" && (
        <div className="text-[10.5px] text-[#444653] mt-1">
          <strong>{est.customer_approval_status === "approved" ? "Approved" : "Rejected"}</strong>
          {est.approved_by && <> by {est.approved_by}</>}
          {est.customer_approval_date && <> · {fmtDateTime(est.customer_approval_date)}</>}
          {est.approval_remarks && <> · {est.approval_remarks}</>}
        </div>
      )}
    </div>
    {est.pdf_attachment_id && (
      <a
        href={avAttachmentUrl(av.id, est.pdf_attachment_id)}
        target="_blank" rel="noopener noreferrer"
        className="text-[11px] inline-flex items-center gap-1 text-yamaha-700 hover:text-yamaha-800 font-semibold"
      >
        <FileSearch className="w-3.5 h-3.5" /> PDF
      </a>
    )}
  </div>
);

const AddEstimateDialog = ({ open, onClose, avId, currentUser, onAdded }) => {
  const [num, setNum] = useState("");
  const [amt, setAmt] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [remarks, setRemarks] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setNum(""); setAmt(""); setDate(new Date().toISOString().slice(0, 10)); setRemarks(""); setFile(null); } }, [open]);

  const submit = async () => {
    if (!num.trim()) return toast.error("Estimate Number is required");
    const a = parseFloat(amt);
    if (!a || a <= 0) return toast.error("Estimate Amount must be > 0");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("estimate_number", num);
      fd.append("estimate_amount", a);
      fd.append("estimate_date", date);
      if (remarks) fd.append("remarks", remarks);
      if (file) fd.append("file", file);
      fd.append("actor", currentUser.name);
      fd.append("actor_role", currentUser.role);
      const updated = await avAddEstimate(avId, fd);
      toast.success("Estimate added");
      onAdded(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add estimate");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(v) => !v && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Add Estimate</AlertDialogTitle>
          <AlertDialogDescription>This becomes the active version. Older estimates stay in history.</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-3 my-2">
          <Field label="Estimate Number" required>
            <input value={num} onChange={(e) => setNum(e.target.value)} className={inputCls} data-testid="av-est-num" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount" required>
              <input type="number" min="0" step="0.01" value={amt} onChange={(e) => setAmt(e.target.value)} className={inputCls} data-testid="av-est-amt" />
            </Field>
            <Field label="Estimate Date">
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
            </Field>
          </div>
          <Field label="Remarks">
            <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} className={inputCls} />
          </Field>
          <Field label="Attach PDF (optional)">
            <input type="file" accept="application/pdf,image/png,image/jpeg,image/webp" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-[12px]" data-testid="av-est-file" />
          </Field>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={submit} disabled={busy} className="bg-yamaha-700 hover:bg-yamaha-800" data-testid="av-est-submit">
            {busy ? "Adding…" : "Add Estimate"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const ManualApproveDialog = ({ state, onClose, avId, currentUser, onDone }) => {
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (state) setRemarks(""); }, [state]);
  if (!state) return null;
  const { est, decision } = state;
  const submit = async () => {
    if (!remarks.trim()) return toast.error("Remarks are mandatory for manual approval");
    setBusy(true);
    try {
      const updated = await avManualApproveEstimate(avId, est.id, {
        decision, remarks,
        actor: currentUser.name, actor_role: currentUser.role,
      });
      toast.success(`Estimate ${decision}`);
      onDone(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <AlertDialog open onOpenChange={(v) => !v && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Manually {decision === "approved" ? "Approve" : "Reject"} Estimate</AlertDialogTitle>
          <AlertDialogDescription>
            Use this when WhatsApp approval isn&apos;t captured. Remarks are mandatory and form part of the audit log.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="my-2">
          <Field label="Remarks" required>
            <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={3} className={inputCls} data-testid="av-est-manual-remarks" />
          </Field>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={submit} disabled={busy}
            data-testid="av-est-manual-confirm"
            className={decision === "approved" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}
          >
            {busy ? "Saving…" : `Mark ${decision}`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const Field = ({ label, required, children }) => (
  <div>
    <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">
      {label}{required && <span className="text-rose-600">*</span>}
    </label>
    {children}
  </div>
);

// ============== DOCUMENTS TAB ==============
export const DocumentsTab = ({ av, currentUser, onChange }) => {
  const canEdit = canEditDocuments(currentUser) && av.status !== "delivered";
  const byKind = useMemo(() => {
    const map = {};
    (av.attachments || []).forEach((a) => {
      if (a.kind && a.kind.startsWith("doc_")) map[a.kind] = a;
    });
    return map;
  }, [av.attachments]);

  return (
    <div className="space-y-4" data-testid="av-tab-documents">
      <div className="text-[14px] font-semibold text-[#1a1b22]">Documents</div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {DOCUMENT_KINDS.map((d) => (
          <DocumentCard
            key={d.kind}
            av={av}
            kind={d.kind}
            label={d.label}
            current={byKind[d.kind]}
            canEdit={canEdit}
            currentUser={currentUser}
            onChange={onChange}
          />
        ))}
      </div>
    </div>
  );
};

const DocumentCard = ({ av, kind, label, current, canEdit, currentUser, onChange }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      fd.append("actor", currentUser.name);
      fd.append("actor_role", currentUser.role);
      await avUploadAttachment(av.id, fd);
      toast.success(`${label} uploaded`);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="border border-[#e2e1eb] rounded-lg overflow-hidden bg-white">
      <div className="px-3 py-2 border-b border-[#e2e1eb] bg-[#faf8ff] flex items-center justify-between">
        <div className="text-[12.5px] font-semibold text-[#1a1b22]">{label}</div>
        <span className={`text-[9.5px] font-bold uppercase tracking-wider rounded-full px-1.5 py-0.5 border ${
          current ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"
        }`}>
          {current ? "Uploaded" : "Pending"}
        </span>
      </div>
      <div className="p-3 space-y-2">
        {current ? (
          <>
            <div className="text-[11.5px] text-[#444653] truncate" title={current.filename}>{current.filename}</div>
            <div className="text-[10.5px] text-[#747684]">
              {current.uploaded_by}{current.uploaded_by_role ? ` (${ROLE_LABELS[current.uploaded_by_role] || current.uploaded_by_role})` : ""} · {fmtDateTime(current.uploaded_at)}
            </div>
            <div className="flex items-center gap-2">
              <a
                href={avAttachmentUrl(av.id, current.id)} target="_blank" rel="noopener noreferrer"
                data-testid={`av-doc-view-${kind}`}
                className="text-[11px] inline-flex items-center gap-1 text-yamaha-700 hover:text-yamaha-800 font-semibold"
              >
                <FileSearch className="w-3 h-3" /> View
              </a>
              {canEdit && (
                <button
                  onClick={() => inputRef.current?.click()}
                  disabled={busy}
                  data-testid={`av-doc-replace-${kind}`}
                  className="text-[11px] inline-flex items-center gap-1 text-amber-700 hover:text-amber-800 font-semibold disabled:opacity-50"
                >
                  <Upload className="w-3 h-3" /> Replace
                </button>
              )}
            </div>
          </>
        ) : (
          <div className="flex items-center justify-between">
            <div className="text-[11px] text-[#747684]">No file uploaded yet.</div>
            {canEdit && (
              <button
                onClick={() => inputRef.current?.click()}
                disabled={busy}
                data-testid={`av-doc-upload-${kind}`}
                className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded bg-yamaha-700 text-white hover:bg-yamaha-800 disabled:opacity-50"
              >
                <Upload className="w-3 h-3" /> Upload
              </button>
            )}
          </div>
        )}
        <input
          ref={inputRef} type="file"
          accept="application/pdf,image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }}
        />
      </div>
    </div>
  );
};

// ============== PARTS TAB ==============
export const PartsTab = ({ av, currentUser, onChange }) => {
  const canEdit = canEditParts(currentUser) && av.status !== "delivered";
  const ps = av.parts_section || {};
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setForm({
      estimate_prepared: !!ps.estimate_prepared,
      parts_ordered: !!ps.parts_ordered,
      order_date: ps.order_date || "",
      expected_delivery_date: ps.expected_delivery_date || "",
      parts_status: ps.parts_status || "not_ordered",
      pymidol_order_no: ps.pymidol_order_no || "",
      remarks: ps.remarks || "",
    });
    setEditing(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      const updated = await avUpdateParts(av.id, {
        ...form,
        actor: currentUser.name, actor_role: currentUser.role,
      });
      toast.success("Parts section updated");
      setEditing(false);
      onChange(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="av-tab-parts">
      <div className="flex items-center justify-between">
        <div className="text-[14px] font-semibold text-[#1a1b22] flex items-center gap-2">
          <ListChecks className="w-4 h-4 text-yamaha-700" /> Parts
        </div>
        {canEdit && !editing && (
          <Button onClick={startEdit} variant="outline" data-testid="av-parts-edit">
            <Pencil className="w-4 h-4 mr-2" /> Edit
          </Button>
        )}
        {editing && (
          <div className="flex gap-2">
            <Button onClick={save} disabled={saving} className="bg-yamaha-700 hover:bg-yamaha-800" data-testid="av-parts-save">
              <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save"}
            </Button>
            <Button onClick={() => setEditing(false)} variant="outline">
              <X className="w-4 h-4 mr-2" /> Cancel
            </Button>
          </div>
        )}
      </div>

      <div className="border border-[#ece9f5] rounded-lg overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 p-3">
          <YesNoRow label="Parts Estimate Prepared" editing={editing} value={editing ? form.estimate_prepared : ps.estimate_prepared} onChange={(v) => setForm((s) => ({ ...s, estimate_prepared: v }))} />
          <YesNoRow label="Parts Ordered" editing={editing} value={editing ? form.parts_ordered : ps.parts_ordered} onChange={(v) => setForm((s) => ({ ...s, parts_ordered: v }))} />
          <Row label="Order Date" value={
            editing ? <input type="date" value={form.order_date} onChange={(e) => setForm((s) => ({ ...s, order_date: e.target.value }))} className={inputCls} /> : (ps.order_date ? fmtDate(ps.order_date) : "—")
          } />
          <Row label="Expected Delivery" value={
            editing ? <input type="date" value={form.expected_delivery_date} onChange={(e) => setForm((s) => ({ ...s, expected_delivery_date: e.target.value }))} className={inputCls} /> : (ps.expected_delivery_date ? fmtDate(ps.expected_delivery_date) : "—")
          } />
          <Row label="Parts Status" value={
            editing ? (
              <select value={form.parts_status} onChange={(e) => setForm((s) => ({ ...s, parts_status: e.target.value }))} className={inputCls} data-testid="av-parts-status">
                {PARTS_STATUSES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            ) : partsStatusLabel(ps.parts_status || "not_ordered")
          } />
          <Row label="Pymidol Order No" value={
            editing ? (
              <input className={`${inputCls} font-mono uppercase`} value={form.pymidol_order_no} onChange={(e) => setForm((s) => ({ ...s, pymidol_order_no: e.target.value.toUpperCase() }))} placeholder="e.g. PYM-2026-…" data-testid="av-parts-pymidol" />
            ) : (ps.pymidol_order_no || "—")
          } />
          <Row wide label="Remarks" value={
            editing ? (
              <textarea value={form.remarks} onChange={(e) => setForm((s) => ({ ...s, remarks: e.target.value }))} rows={2} className={inputCls} />
            ) : (ps.remarks || "—")
          } />
        </div>
      </div>

      <PartsItemsTable av={av} canEdit={canEdit} currentUser={currentUser} onChange={onChange} />
    </div>
  );
};

const YesNoRow = ({ label, editing, value, onChange }) => (
  <div className="flex items-center gap-3">
    <div className="text-[10.5px] font-bold uppercase tracking-wider text-[#747684] w-[40%]">{label}</div>
    {editing ? (
      <div className="flex gap-1.5">
        {[true, false].map((v) => (
          <button
            key={String(v)}
            type="button"
            onClick={() => onChange(v)}
            className={`px-3 py-1 rounded-md border text-[11.5px] font-semibold ${
              value === v ? "border-yamaha-500 bg-yamaha-50 text-yamaha-700" : "border-[#e2e1eb] text-[#444653] hover:bg-[#f4f3fc]"
            }`}
          >
            {v ? "Yes" : "No"}
          </button>
        ))}
      </div>
    ) : (
      <span className="text-[12.5px] text-[#1a1b22]">{value ? "Yes" : "No"}</span>
    )}
  </div>
);

const Row = ({ label, value, wide }) => (
  <div className={`flex items-start gap-3 ${wide ? "md:col-span-2" : ""}`}>
    <div className="text-[10.5px] font-bold uppercase tracking-wider text-[#747684] w-[40%] flex-shrink-0 pt-1.5">{label}</div>
    <div className="text-[12.5px] text-[#1a1b22] flex-1">{value}</div>
  </div>
);

const PartsItemsTable = ({ av, canEdit, currentUser, onChange }) => {
  const items = av.parts_section?.items || [];
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ part_name: "", part_number: "", qty: 1, edd: "", status: "not_ordered" });
  const [busy, setBusy] = useState(false);

  const addItem = async () => {
    if (!draft.part_name.trim()) return toast.error("Part Name is required");
    setBusy(true);
    try {
      await avAddPartItem(av.id, { ...draft, actor: currentUser.name, actor_role: currentUser.role });
      toast.success("Part added");
      setDraft({ part_name: "", part_number: "", qty: 1, edd: "", status: "not_ordered" });
      setAdding(false);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally {
      setBusy(false);
    }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Remove ${item.part_name}?`)) return;
    try {
      await avDeletePartItem(av.id, item.id, { actor: currentUser.name, actor_role: currentUser.role });
      toast.success("Part removed");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="border border-[#e2e1eb] rounded-lg overflow-hidden">
      <div className="px-3 py-2 border-b border-[#e2e1eb] bg-[#faf8ff] flex items-center justify-between">
        <div className="text-[12px] font-semibold text-[#1a1b22]">Parts List ({items.length})</div>
        {canEdit && !adding && (
          <button onClick={() => setAdding(true)} data-testid="av-parts-add-item" className="text-[11px] text-yamaha-700 hover:text-yamaha-800 font-semibold inline-flex items-center gap-1">
            <PlusCircle className="w-3.5 h-3.5" /> Add Part
          </button>
        )}
      </div>
      <table className="w-full text-[12.5px]">
        <thead className="bg-[#faf8ff] text-[10.5px] uppercase tracking-wider text-[#747684]">
          <tr>
            <th className="px-3 py-2 text-left">Part Name</th>
            <th className="px-3 py-2 text-left">Part Number</th>
            <th className="px-3 py-2 text-right">Qty</th>
            <th className="px-3 py-2 text-left">EDD</th>
            <th className="px-3 py-2 text-left">Status</th>
            {canEdit && <th className="px-3 py-2 text-center w-12"></th>}
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && !adding && (
            <tr><td colSpan={canEdit ? 6 : 5} className="px-3 py-6 text-center text-[#747684]">No parts added yet.</td></tr>
          )}
          {items.map((it) => (
            <tr key={it.id} className="border-t border-[#f1f0f7]" data-testid={`av-parts-row-${it.id}`}>
              <td className="px-3 py-1.5">{it.part_name}</td>
              <td className="px-3 py-1.5 font-mono">{it.part_number || "—"}</td>
              <td className="px-3 py-1.5 text-right">{it.qty}</td>
              <td className="px-3 py-1.5">{it.edd ? fmtDate(it.edd) : "—"}</td>
              <td className="px-3 py-1.5">{partsStatusLabel(it.status)}</td>
              {canEdit && (
                <td className="px-3 py-1.5 text-center">
                  <button onClick={() => deleteItem(it)} className="text-rose-600 hover:text-rose-700"><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              )}
            </tr>
          ))}
          {adding && (
            <tr className="border-t border-[#f1f0f7] bg-yamaha-50/30">
              <td className="px-3 py-1.5"><input className={inputCls} value={draft.part_name} onChange={(e) => setDraft({ ...draft, part_name: e.target.value })} placeholder="Part name" data-testid="av-parts-new-name" /></td>
              <td className="px-3 py-1.5"><input className={`${inputCls} font-mono uppercase`} value={draft.part_number} onChange={(e) => setDraft({ ...draft, part_number: e.target.value.toUpperCase() })} placeholder="Optional" /></td>
              <td className="px-3 py-1.5"><input className={`${inputCls} text-right`} type="number" min="1" value={draft.qty} onChange={(e) => setDraft({ ...draft, qty: Math.max(1, parseInt(e.target.value || "1", 10)) })} /></td>
              <td className="px-3 py-1.5"><input className={inputCls} type="date" value={draft.edd} onChange={(e) => setDraft({ ...draft, edd: e.target.value })} /></td>
              <td className="px-3 py-1.5">
                <select className={inputCls} value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })}>
                  {PARTS_STATUSES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </td>
              <td className="px-3 py-1.5 text-center whitespace-nowrap">
                <button onClick={addItem} disabled={busy} className="text-[11px] text-emerald-700 hover:text-emerald-800 font-semibold mr-2" data-testid="av-parts-new-save">Save</button>
                <button onClick={() => setAdding(false)} className="text-[11px] text-[#747684] hover:text-[#444653]">Cancel</button>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

// ============== REPAIR TAB ==============
export const RepairTab = ({ av, currentUser, onChange }) => {
  const canEdit = canEditRepair(currentUser) && av.status !== "delivered";
  const rs = av.repair_section || {};
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);

  // Manual selector only exposes the 4 in-progress statuses; `completed` is
  // set exclusively via the "Work Completed" button so the status nudge to
  // billing_pending is always intentional.
  const REPAIR_SELECTABLE = REPAIR_STATUSES.filter((r) => r.value !== "completed");
  const isCompleted = rs.repair_status === "completed";
  const canMarkCompleted = canEdit && !isCompleted && rs.repair_status === "work_started";

  const startEdit = () => {
    setForm({
      repair_status: rs.repair_status || "not_started",
      expected_completion_date: rs.expected_completion_date || "",
      remarks: rs.remarks || "",
    });
    setEditing(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      const updated = await avUpdateRepair(av.id, {
        ...form, actor: currentUser.name, actor_role: currentUser.role,
      });
      toast.success("Repair section updated");
      setEditing(false);
      onChange(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const markCompleted = async () => {
    if (!window.confirm("Mark repair work as Completed? This will activate the Billing tab.")) return;
    setCompleting(true);
    try {
      const updated = await avUpdateRepair(av.id, {
        repair_status: "completed",
        actor: currentUser.name, actor_role: currentUser.role,
      });
      toast.success("Work Completed · Billing activated");
      onChange(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setCompleting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="av-tab-repair">
      <div className="flex items-center justify-between">
        <div className="text-[14px] font-semibold text-[#1a1b22] flex items-center gap-2">
          <Wrench className="w-4 h-4 text-yamaha-700" /> Repair
        </div>
        {canMarkCompleted && !editing && (
          <Button onClick={markCompleted} disabled={completing} data-testid="av-repair-work-completed" className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <CheckCircle2 className="w-4 h-4 mr-2" /> {completing ? "Saving…" : "Work Completed"}
          </Button>
        )}
        {canEdit && !editing && (
          <Button onClick={startEdit} variant="outline" data-testid="av-repair-edit">
            <Pencil className="w-4 h-4 mr-2" /> Edit
          </Button>
        )}
        {editing && (
          <div className="flex gap-2">
            <Button onClick={save} disabled={saving} className="bg-yamaha-700 hover:bg-yamaha-800" data-testid="av-repair-save">
              <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save"}
            </Button>
            <Button onClick={() => setEditing(false)} variant="outline">
              <X className="w-4 h-4 mr-2" /> Cancel
            </Button>
          </div>
        )}
      </div>

      <div className="border border-[#ece9f5] rounded-lg p-3 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
        <Row label="Repair Status" value={
          editing ? (
            <select value={form.repair_status} onChange={(e) => setForm((s) => ({ ...s, repair_status: e.target.value }))} className={inputCls} data-testid="av-repair-status">
              {REPAIR_SELECTABLE.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          ) : repairStatusLabel(rs.repair_status || "not_started")
        } />
        <Row label="Expected Completion" value={
          editing ? (
            <input type="date" value={form.expected_completion_date} onChange={(e) => setForm((s) => ({ ...s, expected_completion_date: e.target.value }))} className={inputCls} />
          ) : (rs.expected_completion_date ? fmtDate(rs.expected_completion_date) : "—")
        } />
        <Row wide label="Remarks" value={
          editing ? (
            <textarea value={form.remarks} onChange={(e) => setForm((s) => ({ ...s, remarks: e.target.value }))} rows={2} className={inputCls} />
          ) : (rs.remarks || "—")
        } />
        <Row label="Last Updated" value={rs.updated_at ? `${rs.updated_by || "—"} · ${fmtDateTime(rs.updated_at)}` : "—"} />
      </div>
    </div>
  );
};

// ============== TIMELINE TAB ==============
export const TimelineTab = ({ avId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await avTimeline(avId);
        if (!cancelled) setRows(r?.rows || []);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Failed to load timeline");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [avId]);

  return (
    <div className="space-y-3" data-testid="av-tab-timeline">
      <div className="text-[14px] font-semibold text-[#1a1b22] flex items-center gap-2">
        <History className="w-4 h-4 text-yamaha-700" /> Timeline
      </div>
      {loading && <div className="text-[12px] text-[#747684]">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="text-[12px] text-[#747684] py-8 text-center border border-dashed border-[#e2e1eb] rounded-lg">
          No timeline events yet.
        </div>
      )}
      <ol className="relative border-l-2 border-[#ece9f5] pl-4 ml-2 space-y-3">
        {rows.map((r, i) => (
          <li key={r.id || i} className="relative">
            <span className="absolute -left-[22px] top-1.5 w-2.5 h-2.5 rounded-full bg-yamaha-500 ring-4 ring-yamaha-50" />
            <div className="bg-white border border-[#e2e1eb] rounded-lg p-2.5">
              <div className="text-[12.5px] font-semibold text-[#1a1b22]">{timelineActionLabel(r.action)}</div>
              {r.details && <div className="text-[11.5px] text-[#444653] mt-0.5">{r.details}</div>}
              <div className="text-[10.5px] text-[#747684] mt-1">
                {r.actor || "—"}{r.actor_role ? ` (${ROLE_LABELS[r.actor_role] || r.actor_role})` : ""} · {fmtDateTime(r.at)}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
};

// ============== ADVANCE MONEY TAB ==============
export const AdvanceTab = ({ av, currentUser, onChange }) => {
  const canEdit = canEditBilling(currentUser) && av.status !== "delivered";
  const advances = av.advances || [];
  const totalAdvance = (av.billing_derived || {}).total_advance || 0;
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({
    date: new Date().toISOString().slice(0, 10),
    amount: "",
    mode: "cash",
    mr_number: "",
    remarks: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const amt = parseFloat(draft.amount);
    if (!amt || amt <= 0) return toast.error("Amount must be > 0");
    setBusy(true);
    try {
      const updated = await avAddAdvance(av.id, {
        ...draft, amount: amt,
        actor: currentUser.name, actor_role: currentUser.role,
      });
      toast.success("Advance recorded");
      setDraft({ date: new Date().toISOString().slice(0, 10), amount: "", mode: "cash", mr_number: "", remarks: "" });
      setAdding(false);
      onChange(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (adv) => {
    if (!window.confirm(`Remove ₹${adv.amount} (${advanceModeLabel(adv.mode)})?`)) return;
    try {
      const updated = await avDeleteAdvance(av.id, adv.id, { actor: currentUser.name, actor_role: currentUser.role });
      toast.success("Advance removed");
      onChange(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="av-tab-advance">
      <div className="flex items-center justify-between">
        <div className="text-[14px] font-semibold text-[#1a1b22] flex items-center gap-2">
          <Receipt className="w-4 h-4 text-yamaha-700" /> Advance Money
        </div>
        <div className="text-right">
          <div className="text-[10.5px] font-bold uppercase tracking-wider text-[#747684]">Total Advance</div>
          <div className="text-[24px] font-bold text-yamaha-700">{fmtINR(totalAdvance)}</div>
        </div>
      </div>

      <div className="border border-[#e2e1eb] rounded-lg overflow-hidden">
        <div className="px-3 py-2 border-b border-[#e2e1eb] bg-[#faf8ff] flex items-center justify-between">
          <div className="text-[12px] font-semibold text-[#1a1b22]">Payments ({advances.length})</div>
          {canEdit && !adding && (
            <button onClick={() => setAdding(true)} data-testid="av-adv-add-btn" className="text-[11px] text-yamaha-700 hover:text-yamaha-800 font-semibold inline-flex items-center gap-1">
              <PlusCircle className="w-3.5 h-3.5" /> Add Advance
            </button>
          )}
        </div>
        <table className="w-full text-[12.5px]">
          <thead className="bg-[#faf8ff] text-[10.5px] uppercase tracking-wider text-[#747684]">
            <tr>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2 text-left">Mode</th>
              <th className="px-3 py-2 text-left">MR Number</th>
              <th className="px-3 py-2 text-left">Remarks</th>
              <th className="px-3 py-2 text-left">Recorded By</th>
              {canEdit && <th className="px-3 py-2 w-12 text-center"></th>}
            </tr>
          </thead>
          <tbody>
            {advances.length === 0 && !adding && (
              <tr><td colSpan={canEdit ? 7 : 6} className="px-3 py-6 text-center text-[#747684]">No advances recorded yet.</td></tr>
            )}
            {advances.map((a) => (
              <tr key={a.id} className="border-t border-[#f1f0f7]" data-testid={`av-adv-row-${a.id}`}>
                <td className="px-3 py-1.5">{fmtDate(a.date)}</td>
                <td className="px-3 py-1.5 text-right font-semibold">{fmtINR(a.amount)}</td>
                <td className="px-3 py-1.5">{advanceModeLabel(a.mode)}</td>
                <td className="px-3 py-1.5 font-mono text-[11.5px]">{a.mr_number || "—"}</td>
                <td className="px-3 py-1.5 text-[#444653]">{a.remarks || "—"}</td>
                <td className="px-3 py-1.5 text-[10.5px] text-[#747684]">
                  {a.created_by || "—"} · {fmtDateTime(a.created_at)}
                </td>
                {canEdit && (
                  <td className="px-3 py-1.5 text-center">
                    <button onClick={() => remove(a)} className="text-rose-600 hover:text-rose-700" title="Remove">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {adding && (
              <tr className="border-t border-[#f1f0f7] bg-yamaha-50/30">
                <td className="px-3 py-1.5"><input type="date" className={inputCls} value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} data-testid="av-adv-date" /></td>
                <td className="px-3 py-1.5"><input type="number" min="0" step="0.01" className={`${inputCls} text-right`} value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} placeholder="0.00" data-testid="av-adv-amt" /></td>
                <td className="px-3 py-1.5">
                  <select className={inputCls} value={draft.mode} onChange={(e) => setDraft({ ...draft, mode: e.target.value })} data-testid="av-adv-mode">
                    {ADVANCE_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </td>
                <td className="px-3 py-1.5"><input className={`${inputCls} font-mono`} value={draft.mr_number} onChange={(e) => setDraft({ ...draft, mr_number: e.target.value })} placeholder="MR-..." /></td>
                <td className="px-3 py-1.5" colSpan={2}><input className={inputCls} value={draft.remarks} onChange={(e) => setDraft({ ...draft, remarks: e.target.value })} placeholder="Optional" /></td>
                <td className="px-3 py-1.5 text-center whitespace-nowrap">
                  <button onClick={submit} disabled={busy} className="text-[11px] text-emerald-700 hover:text-emerald-800 font-semibold mr-2" data-testid="av-adv-save">Save</button>
                  <button onClick={() => setAdding(false)} className="text-[11px] text-[#747684] hover:text-[#444653]">Cancel</button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="text-[11px] text-[#747684]">Total Advance auto-reflects in the Billing tab.</div>
    </div>
  );
};

// ============== BILLING TAB ==============
export const BillingTab = ({ av, currentUser, onChange }) => {
  const canEdit = canEditBilling(currentUser) && av.status !== "delivered";
  const b = av.billing || {};
  const d = av.billing_derived || {};
  const isInsured = av.claim_type === "insured";
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const pdfRef = useRef(null);

  const startEdit = () => {
    setForm({
      invoice_number: b.invoice_number || "",
      invoice_date: b.invoice_date || "",
      labour_amount: b.labour_amount || 0,
      parts_amount: b.parts_amount || 0,
      insurance_do_amount: b.insurance_do_amount || 0,
      payment_status: b.payment_status || "pending",
    });
    setEditing(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      const updated = await avUpdateBilling(av.id, {
        ...form,
        labour_amount: parseFloat(form.labour_amount) || 0,
        parts_amount: parseFloat(form.parts_amount) || 0,
        insurance_do_amount: parseFloat(form.insurance_do_amount) || 0,
        actor: currentUser.name, actor_role: currentUser.role,
      });
      toast.success("Billing updated");
      setEditing(false);
      onChange(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const uploadInvoice = async (file) => {
    if (!file) return;
    setPdfBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "invoice_pdf");
      fd.append("actor", currentUser.name);
      fd.append("actor_role", currentUser.role);
      const att = await avUploadAttachment(av.id, fd);
      // Then point billing.invoice_pdf_attachment_id at it
      const updated = await avUpdateBilling(av.id, {
        invoice_pdf_attachment_id: att.id,
        actor: currentUser.name, actor_role: currentUser.role,
      });
      toast.success("Invoice PDF uploaded");
      onChange(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setPdfBusy(false);
    }
  };

  // For derived values display we read from `d` (server-computed).
  const jcNet = d.jc_net_amount || 0;
  const totalAdv = d.total_advance || 0;
  const customerPayable = d.customer_payable || 0;
  const refund = d.refund_to_customer || 0;
  const nullify = d.amount_to_be_nullified || 0;
  const receive = d.amount_to_be_received || 0;

  return (
    <div className="space-y-4" data-testid="av-tab-billing">
      <div className="flex items-center justify-between">
        <div className="text-[14px] font-semibold text-[#1a1b22] flex items-center gap-2">
          <Receipt className="w-4 h-4 text-yamaha-700" /> Billing
          <span className={`ml-2 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 border ${isInsured ? "bg-yamaha-50 text-yamaha-700 border-yamaha-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
            {isInsured ? "Insured" : "Non-Insured"}
          </span>
        </div>
        {canEdit && !editing && (
          <Button onClick={startEdit} variant="outline" data-testid="av-billing-edit">
            <Pencil className="w-4 h-4 mr-2" /> Edit
          </Button>
        )}
        {editing && (
          <div className="flex gap-2">
            <Button onClick={save} disabled={saving} className="bg-yamaha-700 hover:bg-yamaha-800" data-testid="av-billing-save">
              <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save"}
            </Button>
            <Button onClick={() => setEditing(false)} variant="outline"><X className="w-4 h-4 mr-2" /> Cancel</Button>
          </div>
        )}
      </div>

      {/* Editable invoice fields */}
      <div className="border border-[#ece9f5] rounded-lg p-3 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
        <Row label="Invoice Number" value={
          editing ? <input className={inputCls} value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} data-testid="av-billing-invoice-no" /> : (b.invoice_number || "—")
        } />
        <Row label="Invoice Date" value={
          editing ? <input type="date" className={inputCls} value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} /> : (b.invoice_date ? fmtDate(b.invoice_date) : "—")
        } />
        <Row label="Labour Amount" value={
          editing ? <input type="number" min="0" step="0.01" className={`${inputCls} text-right`} value={form.labour_amount} onChange={(e) => setForm({ ...form, labour_amount: e.target.value })} data-testid="av-billing-labour" /> : fmtINR(b.labour_amount)
        } />
        <Row label="Parts Amount" value={
          editing ? <input type="number" min="0" step="0.01" className={`${inputCls} text-right`} value={form.parts_amount} onChange={(e) => setForm({ ...form, parts_amount: e.target.value })} data-testid="av-billing-parts" /> : fmtINR(b.parts_amount)
        } />
        {isInsured && (
          <Row label="Insurance DO Amount" value={
            editing ? <input type="number" min="0" step="0.01" className={`${inputCls} text-right`} value={form.insurance_do_amount} onChange={(e) => setForm({ ...form, insurance_do_amount: e.target.value })} data-testid="av-billing-do" /> : fmtINR(b.insurance_do_amount)
          } />
        )}
        <Row label="Payment Status" value={
          editing ? (
            <select className={inputCls} value={form.payment_status} onChange={(e) => setForm({ ...form, payment_status: e.target.value })} data-testid="av-billing-payment-status">
              {PAYMENT_STATUSES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          ) : paymentStatusLabel(b.payment_status || "pending")
        } />
      </div>

      {/* Derived summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <DerivedTile label="JC Net (Labour + Parts)" value={fmtINR(jcNet)} tone="neutral" />
        <DerivedTile label="Total Advance" value={fmtINR(totalAdv)} tone="yamaha" />
        {!isInsured && <DerivedTile label="Customer Payable" value={fmtINR(customerPayable)} tone="emerald" big />}
        {isInsured && receive > 0 && <DerivedTile label="Amount To Be Received" value={fmtINR(receive)} tone="emerald" big />}
        {isInsured && refund > 0 && <DerivedTile label="Refund to Customer" value={fmtINR(refund)} tone="amber" big />}
        {isInsured && nullify > 0 && (
          <DerivedTile label="Amount To Be Nullified" value={fmtINR(nullify)} tone="rose" big sub="Must be 0 to close AV" />
        )}
        {isInsured && nullify === 0 && refund === 0 && receive === 0 && (
          <DerivedTile label="Settlement" value="Balanced" tone="emerald" big sub="Ready for delivery" />
        )}
      </div>

      {nullify > 0 && (
        <div className="border border-rose-200 bg-rose-50/50 rounded-lg p-3 flex items-start gap-2 text-[12px] text-rose-800" data-testid="av-billing-closure-warning">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Closure blocked:</strong> Amount to be Nullified is {fmtINR(nullify)}. Process the refund (or adjust billing) before marking this AV as Delivered.
          </div>
        </div>
      )}

      {/* Invoice PDF */}
      <div className="border border-[#ece9f5] rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[12px] font-semibold text-[#1a1b22]">Invoice PDF</div>
          {canEdit && (
            <button onClick={() => pdfRef.current?.click()} disabled={pdfBusy} data-testid="av-billing-upload-invoice" className="text-[11px] inline-flex items-center gap-1 text-yamaha-700 hover:text-yamaha-800 font-semibold disabled:opacity-50">
              <Upload className="w-3 h-3" /> {b.invoice_pdf_attachment_id ? "Replace" : "Upload"}
            </button>
          )}
          <input ref={pdfRef} type="file" accept="application/pdf,image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { uploadInvoice(e.target.files?.[0]); e.target.value = ""; }} />
        </div>
        {b.invoice_pdf_attachment_id ? (
          <a href={avAttachmentUrl(av.id, b.invoice_pdf_attachment_id)} target="_blank" rel="noopener noreferrer" className="text-[11.5px] inline-flex items-center gap-1 text-yamaha-700 hover:text-yamaha-800 font-semibold">
            <FileSearch className="w-3.5 h-3.5" /> View Invoice PDF
          </a>
        ) : (
          <div className="text-[11px] text-[#747684]">No invoice PDF uploaded.</div>
        )}
      </div>
    </div>
  );
};

const DerivedTile = ({ label, value, tone, big, sub }) => {
  const tones = {
    neutral: "border-[#e2e1eb] bg-[#faf8ff] text-[#1a1b22]",
    yamaha: "border-yamaha-200 bg-yamaha-50 text-yamaha-800",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    rose: "border-rose-200 bg-rose-50 text-rose-800",
  };
  return (
    <div className={`border rounded-lg p-3 ${tones[tone] || tones.neutral}`}>
      <div className="text-[10.5px] font-bold uppercase tracking-wider opacity-70">{label}</div>
      <div className={`mt-0.5 font-bold ${big ? "text-[20px]" : "text-[15px]"}`}>{value}</div>
      {sub && <div className="text-[10.5px] opacity-80 mt-1">{sub}</div>}
    </div>
  );
};

// ============== CRM NOTES TAB ==============
export const CrmNotesTab = ({ av, currentUser, onChange }) => {
  const canAdd = canAddCrmNote(currentUser) && av.status !== "delivered";
  const canDelete = canDeleteCrmNote(currentUser);
  const notes = useMemo(
    () => [...(av.crm_notes || [])].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")),
    [av.crm_notes],
  );

  const today = new Date().toISOString().slice(0, 10);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({
    follow_up_date: today, note: "", next_follow_up_date: "", customer_response: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!draft.note.trim()) return toast.error("Note text is required");
    if (!draft.follow_up_date) return toast.error("Follow-up Date is required");
    setBusy(true);
    try {
      const updated = await avAddCrmNote(av.id, {
        ...draft,
        next_follow_up_date: draft.next_follow_up_date || null,
        customer_response: draft.customer_response || null,
        actor: currentUser.name, actor_role: currentUser.role,
      });
      toast.success("Note added");
      setDraft({ follow_up_date: today, note: "", next_follow_up_date: "", customer_response: "" });
      setAdding(false);
      onChange(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (note) => {
    if (!window.confirm("Delete this CRM note? This cannot be undone.")) return;
    try {
      const updated = await avDeleteCrmNote(av.id, note.id, { actor: currentUser.name, actor_role: currentUser.role });
      toast.success("Note deleted");
      onChange(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="av-tab-crm">
      <div className="flex items-center justify-between">
        <div className="text-[14px] font-semibold text-[#1a1b22] flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-yamaha-700" /> CRM Notes
        </div>
        {canAdd && !adding && (
          <Button onClick={() => setAdding(true)} data-testid="av-crm-add-btn" className="bg-yamaha-700 hover:bg-yamaha-800">
            <PlusCircle className="w-4 h-4 mr-2" /> Add Note
          </Button>
        )}
      </div>

      {/* Add form */}
      {adding && (
        <div className="border border-yamaha-200 bg-yamaha-50/30 rounded-lg p-3 space-y-3" data-testid="av-crm-add-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Follow-up Date" required>
              <input type="date" value={draft.follow_up_date} onChange={(e) => setDraft({ ...draft, follow_up_date: e.target.value })} className={inputCls} data-testid="av-crm-fu-date" />
            </Field>
            <Field label="Next Follow-up Date">
              <input type="date" value={draft.next_follow_up_date} onChange={(e) => setDraft({ ...draft, next_follow_up_date: e.target.value })} className={inputCls} data-testid="av-crm-next-date" />
            </Field>
          </div>
          <Field label="Note" required>
            <textarea value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} rows={3} className={inputCls} placeholder="What did you discuss with the customer?" data-testid="av-crm-note" />
          </Field>
          <Field label="Customer Response">
            <textarea value={draft.customer_response} onChange={(e) => setDraft({ ...draft, customer_response: e.target.value })} rows={2} className={inputCls} placeholder="What did the customer say?" data-testid="av-crm-response" />
          </Field>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setAdding(false)} variant="outline" disabled={busy}>Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-yamaha-700 hover:bg-yamaha-800" data-testid="av-crm-save">
              {busy ? "Saving…" : "Save Note"}
            </Button>
          </div>
        </div>
      )}

      {/* History */}
      <div className="border border-[#e2e1eb] rounded-lg overflow-hidden">
        <div className="px-3 py-2 border-b border-[#e2e1eb] bg-[#faf8ff] text-[12px] font-semibold text-[#1a1b22]">
          History ({notes.length})
        </div>
        {notes.length === 0 && (
          <div className="px-4 py-8 text-center text-[#747684] text-[12px]">
            No CRM notes yet. {canAdd && "Click \"Add Note\" to capture a follow-up."}
          </div>
        )}
        {notes.map((n) => (
          <div key={n.id} className="px-3 py-2.5 border-b last:border-b-0 border-[#f1f0f7]" data-testid={`av-crm-note-${n.id}`}>
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-[11px] text-[#444653] flex-wrap">
                  <span className="inline-flex items-center gap-1">
                    <CalendarIcon className="w-3 h-3" /> {fmtDate(n.follow_up_date)}
                  </span>
                  {n.next_follow_up_date && (
                    <span className="inline-flex items-center gap-1 text-amber-700">
                      <CalendarIcon className="w-3 h-3" /> Next: {fmtDate(n.next_follow_up_date)}
                    </span>
                  )}
                </div>
                <div className="text-[13px] text-[#1a1b22] mt-1 whitespace-pre-line">{n.note}</div>
                {n.customer_response && (
                  <div className="text-[12px] text-[#444653] mt-1.5 bg-[#faf8ff] border-l-2 border-yamaha-300 pl-2 py-1 italic whitespace-pre-line">
                    “{n.customer_response}”
                  </div>
                )}
                <div className="text-[10.5px] text-[#747684] mt-1.5">
                  {n.created_by || "—"}{n.created_by_role ? ` (${ROLE_LABELS[n.created_by_role] || n.created_by_role})` : ""} · {fmtDateTime(n.created_at)}
                </div>
              </div>
              {canDelete && (
                <button onClick={() => remove(n)} className="text-rose-600 hover:text-rose-700 flex-shrink-0" title="Delete (admin only)" data-testid={`av-crm-delete-${n.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
